import { TrackLocation } from "data/records";
import { Record } from "immutable";
import { pointAverageObject } from "lib/geoUtils";
import { softError } from "lib/softError";

// flexible descriptor for map viewports. center/zoom and bounds are
// mutually exclusive.
export const Viewport = Record({ center: null, zoom: null, bounds: null });

// convenience function to assert that an object is latlng duck type
const ll = (o) => o && o.lat && o.lng;

Viewport.validate = (vp) => {
  try {
    // viewport is valid if center/zoom are provided, but bounds is absent
    if (vp.center && ll(vp.center) && vp.zoom && !vp.bounds) {
      return true;
    }
    // viewport is valid if bounds is provided, but center/zoom is absent
    if (!vp.center && vp.bounds && ll(vp.bounds[0]) && ll(vp.bounds[1])) {
      return true;
    }
    // if the above conditions are not met, it is not a valid viewport
    return false;
  } catch (e) {
    return false;
  }
};

Viewport.getCenter = (vp) => {
  if (vp.center) {
    return vp.center;
  }
  if (vp.bounds) {
    return pointAverageObject(vp.bounds);
  }
  softError("could not determine center for viewport", vp);
  return null;
};

Viewport.containingSlice = (highlightSlice, trackPoints) => {
  const slicePoints = trackPoints.slice(
    TrackLocation.closestIndex(highlightSlice[0]),
    TrackLocation.closestIndex(highlightSlice[1])
  );

  const NE = {
    lng: slicePoints.reduce((min, p) => (p.lng && p.lng < min ? p.lng : min), Infinity),
    lat: slicePoints.reduce((min, p) => (p.lat && p.lat < min ? p.lat : min), Infinity),
  };
  const SW = {
    lng: slicePoints.reduce((max, p) => (p.lng && p.lng > max ? p.lng : max), -Infinity),
    lat: slicePoints.reduce((max, p) => (p.lat && p.lat > max ? p.lat : max), -Infinity),
  };

  return Viewport({ bounds: [NE, SW] });
};

Viewport.containingAll = (troutes) => {
  const slicePoints = troutes
    .valueSeq()
    .map((t) => t.trackPoints)
    .toArray()
    .flat();

  const SW = {
    lng: slicePoints.reduce((min, p) => (p.lng && p.lng < min ? p.lng : min), Infinity),
    lat: slicePoints.reduce((min, p) => (p.lat && p.lat < min ? p.lat : min), Infinity),
  };
  const NE = {
    lng: slicePoints.reduce((max, p) => (p.lng && p.lng > max ? p.lng : max), -Infinity),
    lat: slicePoints.reduce((max, p) => (p.lat && p.lat > max ? p.lat : max), -Infinity),
  };

  return Viewport({ bounds: [SW, NE] });
};

Viewport.combine = (...viewports) => {
  const SW = {
    lng: viewports.reduce((min, vp) => (vp.bounds[0].lng && vp.bounds[0].lng < min ? vp.bounds[0].lng : min), Infinity),
    lat: viewports.reduce((min, vp) => (vp.bounds[0].lat && vp.bounds[0].lat < min ? vp.bounds[0].lat : min), Infinity),
  };
  const NE = {
    lng: viewports.reduce(
      (max, vp) => (vp.bounds[1].lng && vp.bounds[1].lng > max ? vp.bounds[1].lng : max),
      -Infinity
    ),
    lat: viewports.reduce(
      (max, vp) => (vp.bounds[1].lat && vp.bounds[1].lat > max ? vp.bounds[1].lat : max),
      -Infinity
    ),
  };

  return Viewport({ bounds: [SW, NE] });
};

Viewport.fromGoogleLatLngBounds = (googleLatLngBounds) => {
  const { lat: swLat, lng: swLng } = googleLatLngBounds.getSouthWest();
  const { lat: neLat, lng: neLng } = googleLatLngBounds.getNorthEast();

  const SW = {
    lng: swLng(),
    lat: swLat(),
  };
  const NE = {
    lng: neLng(),
    lat: neLat(),
  };

  return Viewport({ bounds: [SW, NE] });
};
